import type { TableDataShape } from '@utilities/components/TableData/utilities/types';
import type { ProportionStatGroup } from '@utilities/helpers/significance';

import {
  COMPARE_BY_ACCESSOR,
  TOTAL_RESPONSES_ACCESSOR,
} from '@api/survey_dashboard/survey_responses/utilities/columns';
import { computeProportionStatTest, indexToColumnLetter } from '@utilities/helpers/significance';

export const GRID_SEGMENT_ACCESSOR = COMPARE_BY_ACCESSOR;
export const GRID_BASE_ACCESSOR = TOTAL_RESPONSES_ACCESSOR;

const NON_OPTION_ACCESSORS = ['option_value', GRID_SEGMENT_ACCESSOR, GRID_BASE_ACCESSOR];

const getStatementKey = (row: Record<string, unknown> | undefined): string => {
  const statement = row?.sub_question_id ?? row?.option_id ?? row?.option_value;
  return statement == null || statement === '' ? '' : String(statement);
};

export const getGridSignificanceRowKey = (row: Record<string, unknown> | undefined): string => {
  const statementKey = getStatementKey(row);
  const segment = row?.[GRID_SEGMENT_ACCESSOR];
  if (statementKey === '' || segment == null || segment === '') return '';
  return `${statementKey}\u001F${String(segment)}`;
};

export interface GridSignificanceResult {
  cellHigherThanByKey: Record<string, Record<string, string[]>>;
  optionAccessors: string[];
  rowLetterByKey: Record<string, null | string>;
}

/**
 * Adapts a grid `tableCount` table for significance testing and maps the
 * results back to the row and answer-option keys used when rendering the grid.
 *
 * Rows are grouped by statement, while compare-by segments are kept in a
 * consistent first-appearance order across every statement. This lets each
 * segment use the same identifying letter throughout the table, even when a
 * statement does not include every segment.
 *
 * @param tableCount - The grid's count table, including its columns and rows.
 * @param alpha - The significance level used by the proportion test.
 * @returns The tested option accessors and annotations keyed for direct lookup
 * by the grid renderer, or `null` when the table cannot be mapped to a reliable
 * set of comparisons.
 */
export function computeGridSignificance(
  tableCount: null | TableDataShape | undefined,
  alpha?: number
): GridSignificanceResult | null {
  const columns = tableCount?.columns;
  const rows = tableCount?.data;
  if (!columns?.length || !rows?.length) return null;

  const accessors = columns
    .map((column) => column.accessor)
    .filter((accessor): accessor is string => typeof accessor === 'string');
  if (!accessors.includes(GRID_SEGMENT_ACCESSOR) || !accessors.includes(GRID_BASE_ACCESSOR)) {
    return null;
  }
  const optionAccessors = accessors.filter((accessor) => !NON_OPTION_ACCESSORS.includes(accessor));
  if (!optionAccessors.length) return null;

  const segmentIndexByName = new Map<string, number>();
  const statements = new Map<
    string,
    { row: Record<string, unknown>; rowKey: string; segmentIndex: number }[]
  >();

  for (const row of rows) {
    const rowKey = getGridSignificanceRowKey(row);
    const statementKey = getStatementKey(row);
    if (rowKey === '') return null;

    const segmentName = String(row[GRID_SEGMENT_ACCESSOR]);
    if (!segmentIndexByName.has(segmentName)) {
      segmentIndexByName.set(segmentName, segmentIndexByName.size);
    }
    const segmentIndex = segmentIndexByName.get(segmentName) as number;

    const statementRows = statements.get(statementKey) ?? [];

    if (statementRows.some((entry) => entry.rowKey === rowKey)) return null;
    statementRows.push({ row, rowKey, segmentIndex });
    statements.set(statementKey, statementRows);
  }

  if (segmentIndexByName.size < 2) return null;

  const segmentCount = segmentIndexByName.size;
  const cellHigherThanByKey: Record<string, Record<string, string[]>> = Object.create(null);
  const rowLetterByKey: Record<string, null | string> = Object.create(null);

  statements.forEach((statementRows) => {
    const groups: ProportionStatGroup[] = Array.from({ length: segmentCount }, () => ({
      n: 0,
      options: [],
    }));
    for (const { row, segmentIndex } of statementRows) {
      groups[segmentIndex] = {
        n: toFiniteNumber(row[GRID_BASE_ACCESSOR]) ?? 0,
        options: optionAccessors.map((accessor) => ({ count: toFiniteNumber(row[accessor]) })),
      };
    }

    const result = computeProportionStatTest({ groups }, alpha);

    for (const { rowKey, segmentIndex } of statementRows) {
      rowLetterByKey[rowKey] = result ? indexToColumnLetter(segmentIndex) : null;

      const cellLetters: Record<string, string[]> = Object.create(null);
      if (result) {
        optionAccessors.forEach((accessor, optionIndex) => {
          const letters = result.cellHigherThan[optionIndex]?.[segmentIndex] ?? [];
          if (letters.length) cellLetters[accessor] = letters;
        });
      }
      cellHigherThanByKey[rowKey] = cellLetters;
    }
  });

  return { cellHigherThanByKey, optionAccessors, rowLetterByKey };
}

function toFiniteNumber(value: unknown): null | number {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
